var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { $accounts } from 'ziphy-web-shared/basic/entities/accounts';
import { $auth } from 'ziphy-web-shared/basic/entities/auth';
import { Button } from 'ziphy-web-shared/basic/lib/buttons';
import { useForm } from 'ziphy-web-shared/basic/lib/forms';
import { LegacyInput, PhoneInput } from 'ziphy-web-shared/basic/lib/forms/fields';
import { $awsCaptcha, AwsCaptcha, CodeResenderCallMeButton, CodeResenderInput, CodeResenderResendButton, showAlert, useCodeResender, } from 'ziphy-web-shared/basic/lib/utilities';
import { Modal, ModalToolbarBtn } from 'ziphy-web-shared/basic/lib/utilities/modals';
import { $loader } from 'ziphy-web-shared/basic/utils';
import mainConfig from '@config/main';
const SUBMIT_LOADER = 'add-contact-submit-handle';
const AddAccount = ({ accountType, value, setView, _core }) => {
    const { t } = useTranslation();
    const isActivate = !!value;
    const formPhone = useForm({
        account: { value: value || '', rules: [{ name: 'phone', required: true }], output: ['phone'] },
    });
    const formEmail = useForm({
        account: { value: value, rules: ['email', 'required'] },
    });
    const { form: accountForm } = accountType === 'phone' ? formPhone : formEmail;
    const { account } = accountForm.fields;
    const codeResender = useCodeResender({
        onSubmit: useCallback((messageType) => __awaiter(void 0, void 0, void 0, function* () {
            return yield $auth.sendCode({
                type: accountType,
                using: messageType,
                value: account.getOutputValue(),
            });
        }), [accountType]),
        clearDeps: [accountType, accountForm.values],
    });
    const clearAll = useCallback(() => {
        account.onChange('');
        codeResender.clear();
    }, [accountType]);
    const handleSubmitCode = $loader.registerHandler(SUBMIT_LOADER, (e) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        e.preventDefault();
        if (accountForm.validateAll().isInvalid)
            return;
        _core === null || _core === void 0 ? void 0 : _core.onBlock();
        const response = yield handleSendTextCode();
        if (!$awsCaptcha.enabled) {
            if (((_a = response.prepared) === null || _a === void 0 ? void 0 : _a.isBound) && !isActivate) {
                showAlert.error(t(`alert.account_exists.${accountType}`), { isApiMessage: true });
                clearAll();
            }
            else if (((_c = (_b = response.error) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.reason) === 'Incorrect phone number') {
                accountForm.forceError('account');
            }
            else if ((_d = response.error) === null || _d === void 0 ? void 0 : _d.message) {
                showAlert.error(response.error.message, { isApiMessage: true });
            }
        }
        _core === null || _core === void 0 ? void 0 : _core.onUnblock();
    }));
    const handleSubmitDone = $loader.registerHandler(SUBMIT_LOADER, (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        if (codeResender.form.validateAll().isInvalid || accountForm.validateAll().isInvalid)
            return;
        _core.onBlock();
        const response = yield $accounts.create({
            type: accountType,
            value: account.getOutputValue(),
            code: codeResender.code.getOutputValue(),
        });
        if (response.prepared) {
            setView('list');
        }
        else if (response.error) {
            codeResender.form.forceError('code');
        }
        _core.onUnblock();
    }));
    const handleSendTextCode = useCallback(() => __awaiter(void 0, void 0, void 0, function* () { return yield codeResender.sendCode('text'); }), [codeResender.sendCode]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ className: "modal-toolbar" }, { children: [_jsx(ModalToolbarBtn, { type: "back", action: () => setView('list') }), _jsx(ModalToolbarBtn, { type: "close", action: _core.onHide })] })), _jsx(Modal.Header, Object.assign({ className: "text-lg-center" }, { children: t(accountType === 'phone' ? 'label.add_phone' : 'label.add_email') })), _jsx(Modal.Body, { children: _jsxs("form", Object.assign({ id: "profile_contacts_form", onSubmit: codeResender.isWaiting ? handleSubmitDone : handleSubmitCode, noValidate: true }, { children: [accountType === 'phone' ? (_jsx(PhoneInput, { mode: "md", className: "mb-10", field: account })) : (_jsx(LegacyInput, { field: account, type: "email", placeholder: t('placeholder.empty_email'), clear: !isActivate, readonly: isActivate, className: "mb-10", mode: "noLabel" }, "inputEmail")), _jsx(AwsCaptcha, { show: $awsCaptcha.enabled, apiKey: mainConfig.awsCaptcha.key, domainList: mainConfig.awsCaptcha.domains }), codeResender.isWaiting && (_jsx(CodeResenderInput, { code: codeResender.code, messageType: codeResender.messageType, className: 'mt-10' })), _jsx("div", Object.assign({ className: "mt-10" }, { children: _jsx(Button, Object.assign({ mode: ['primary', 'block'], form: "profile_contacts_form", isLoading: $loader.isRunHandler(SUBMIT_LOADER) }, { children: codeResender.isWaiting ? t('btn.done') : t('btn.send_code') })) })), codeResender.isWaiting && (_jsx(CodeResenderResendButton, { initTime: codeResender.codeSent, onResend: handleSendTextCode, className: 'mt-10' })), codeResender.isWaiting && accountType === 'phone' && (_jsx(CodeResenderCallMeButton, { onClick: codeResender.callMe, className: 'mt-20' }))] })) })] }));
};
export default observer(AddAccount);
